import { Component, Input, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { UserRoles } from '../settings.component';
import { Entity } from '@ex/module/core/data-models/Entity';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { NestedTreeControl } from '@angular/cdk/tree';
import { BaseComponent } from '@ex/module/core/domain/base-component';
import { CompanyPermission, products } from 'src/app/core/data-models/company-permission';
import { BehaviorSubject, Subscription } from 'rxjs';

@Component({
  selector: 'company-tree',
  templateUrl: './company-tree.component.html',
  styleUrl: './company-tree.component.scss'
})
export class CompanyTreeComponent extends BaseComponent implements OnInit, OnDestroy {
  selectedUserRole!: UserRoles
  @Input('selectedUserRole') set setSelectedUserRole(val: UserRoles) {
    this.selectedUserRole = val;
    if (val == UserRoles.Owner) {
      this.products = ['action_planning']
    }else{
      this.initUserProductPermission();
    }
  };
  @Input() permissions: CompanyPermission[] = [];
  @Input() language: any;

  private _allEntities: Entity[] = [];
  @Input() set allEntities(val: Entity[]) {
    this._allEntities = val || [];
    this.filterPermission('');
  }
  get allEntities(): Entity[] { return this._allEntities.filter(x=> !x.isDeleted); }

  @ViewChild('tree') tree: any;

  products: string[] = [];
  entitiesNavigation$ = new BehaviorSubject<Entity[]>([]);
  entitiesNavigation: Entity[] = [];
  permissionDataSource = new MatTreeNestedDataSource<Entity>();
  permissionTreeControl = new NestedTreeControl<Entity>(node => this.entitiesNavigation.filter(x => x.parentKey == node.key));
  private subs = new Subscription();

  UserRoles = UserRoles;

  hasChild = (_: number, node: Entity) => {
    return this.entitiesNavigation.filter(x => x.parentKey == node.key).length > 0;
  }

  ngOnInit() {
    // this.initUserProductPermission();
    this.subs.add(
      this.entitiesNavigation$.subscribe(data => {
        this.entitiesNavigation = data;
        this.permissionDataSource.data = this.entitiesNavigation.filter(x => !x.parentKey);
      })
    );
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  initUserProductPermission() {
    this.products = [];
    for (var prod in products) {
      // Culture hidden for the AI Readiness release; drop the && to bring it back.
      if (prod != 'Psychometric')
        this.products.push((products as any)[prod]);
    }
  }

  isProduct(prod: string) {
    if (prod.includes('settings') || prod.includes('overview') || prod.includes('action_planning')) {
      return false;
    }
    return true;
  }

  getProductIcon(product: string) {
    if (product == 'ONBOARDING') product = 'onboarding';
    else if (product == 'ENGAGEMENT') product = 'Engagement';
    else if (product == 'HAPPINESS') product = 'Happiness';
    else if (product == 'EXIT') product = 'Exit';
    else if (product == products.Psychometric) product = 'Psychomatric';
    else if (product == products.SurveyX) product = 'SurveyX';

    return '/assets/img/sub/' + product + '.svg';
  }

  getCompanyPermission(entityKey: string): CompanyPermission {
    if (!this.permissions) this.permissions = [];
    let permission = this.permissions.find(f => f.key == entityKey);
    if (!permission) {
      permission = { key: entityKey, products: [] };
      this.permissions.push(permission);
    }
    return permission;
  }

  handleClickAll(entityKey: string) {
    let permission: CompanyPermission = this.getCompanyPermission(entityKey);
    if (permission?.products?.length != this.products?.length) {
      permission.products = JSON.parse(JSON.stringify(this.products));
    } else {
      permission.products = [];
    }
  }

  permissionHandle(product: string, entityKey: string) {
    let permission: CompanyPermission = this.getCompanyPermission(entityKey);
    if (!permission?.products) permission.products = [];
    if (!permission?.products?.find(prod => product == prod)) {
      permission.products?.push(product);
    } else {
      if (permission.products.indexOf(product) > -1) {
        permission.products.splice(permission.products.indexOf(product), 1);
      }
    }
  }

  permissionCheck(product: string, entityKey: string) {
    let permission = this.getCompanyPermission(entityKey);
    if (permission?.products?.length) {
      return permission.products.find(prod => product == prod);
    }
    return undefined;
  }

  handleClickAllNested(entityKey: string) {
    let Parentpermission: CompanyPermission = this.getCompanyPermission(entityKey);
    let childrens = this.entitiesNavigation.filter(x => x.parentKey == entityKey);
    childrens.forEach(child => {
      let childpermission: CompanyPermission = this.getCompanyPermission(child.key);
      childpermission.products = JSON.parse(JSON.stringify(Parentpermission.products));
      if (this.entitiesNavigation.filter(x => x.parentKey == child.key).length > 0)
        this.handleClickAllNested(child.key);
    });
  }

  permissionHandleNested(product: string, entityKey: string) {
    let Parentpermission: CompanyPermission = this.getCompanyPermission(entityKey);
    let checked = Parentpermission?.products?.find(f => f == product);
    let childrens = this.entitiesNavigation.filter(x => x.parentKey == entityKey);
    childrens.forEach(child => {
      let childpermission: CompanyPermission = this.getCompanyPermission(child.key);
      if (!childpermission?.products) childpermission.products = [];
      if (checked) {
        if (!childpermission?.products?.find(prod => product == prod))
          childpermission.products?.push(product);
      } else {
        if (childpermission?.products?.find(prod => product == prod))
          childpermission.products.splice(childpermission.products.indexOf(product), 1);
      }
      if (this.entitiesNavigation.filter(x => x.parentKey == child.key).length > 0)
        this.permissionHandleNested(product, child.key);
    });
  }

  filterPermission(value: string) {
    this.entitiesNavigation$.next([]);
    if (value) {
      let items = JSON.parse(JSON.stringify(this.allEntities));
      let filtered = items?.filter(x => {
        let name = "";
        if (x.name) {
          if (typeof x.name === 'string') name = x.name;
          else if (this.language && x.name[this.language]) name = x.name[this.language];
          else if (x.name['en']) name = x.name['en'];
          else name = Object.values(x.name)[0] as string;
        }
        return name.toLowerCase().includes(value.toLowerCase());
      });
      let childToParentNodes: Entity[] = [];
      if (filtered.length > 0) {
        filtered.forEach(node => {
          (node as any)['highlight'] = true;
          childToParentNodes.push(node);
          childToParentNodes = this.findParents(node, childToParentNodes);
        });
      }
      this.entitiesNavigation$.next(childToParentNodes);
      this.permissionTreeControl.expandAll();
    } else {
      this.entitiesNavigation$.next(this.allEntities);
      this.permissionTreeControl.collapseAll();
    }
  }

  private findParents(node: Entity, nodes: Entity[] = []) {
    var parent = this.allEntities.find(x => x.key == node.parentKey);
    if (parent) {
      if (!nodes.find(x => x.key == parent.key)) {
        nodes.push(JSON.parse(JSON.stringify(parent)));
        nodes = this.findParents(parent, nodes);
      }
    }
    return nodes;
  }
}
